import { AppState } from "@/store/root.reducer";
import { Space } from "@/models/space.model";
import { useDispatch, useSelector } from "react-redux";
import { GetSpaces, GetUsers, AssignMoodToUser, UnassignMoodFromUser } from "./store/admin.actions";
import React, { useState } from "react";
import User from "@/models/user.model";
import { SpaceData } from "@/types/spaceData";
import { Moods } from "@/types/moodsData";
import Button from "../../components/molecules/Button";

export const getAssignableMoodEntries = (moods: Moods[]) => moods
    .map((mood, index) => ({ mood, index }))
    .filter(({ index }) => index !== 0);

export const AssignMoods = () => {
    const dispatch = useDispatch();
    const spaces: SpaceData[] = useSelector((state: AppState) => state.admin.spaces);
    const users: User[] = useSelector((state: AppState) => state.admin.users);
    const loading: boolean = useSelector((state: AppState) => state.admin.loading);

    const [selectedSpace, setSelectedSpace] = useState<SpaceData | null>(null);
    const [selectedMood, setSelectedMood] = useState<number | null>(null);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [assigning, setAssigning] = useState<boolean>(false);
    const [unassigning, setUnassigning] = useState<boolean>(false);

    React.useEffect(() => {
        (async () => {
            dispatch(GetSpaces());
            dispatch(GetUsers());
        })();
    }, []);

    React.useEffect(() => {
        if (!loading) {
            setAssigning(false);
            setUnassigning(false);
        }
    }, [loading]);

    // Keep selectedSpace in sync with store updates (e.g., after assign/unassign)
    React.useEffect(() => {
        if (selectedSpace) {
            const freshSpace = spaces.find(s => s._id === selectedSpace._id) ?? null;
            if (freshSpace && freshSpace !== selectedSpace) {
                setSelectedSpace(freshSpace);
            }
        }
    }, [spaces]);

    if (spaces.length === 0 || users.length === 0) {
        return (
            <div className="container">
                <h6>Assign Moods</h6>
                <p>Loading...</p>
            </div>
        )
    }

    const assignedUserIds = selectedMood === null
        ? []
        : selectedSpace?.data.moods[selectedMood]?.users ?? [];
    const selectedAssignableIds = selectedUserIds.filter(id => !assignedUserIds.includes(id));
    const selectedUnassignableIds = selectedUserIds.filter(id => assignedUserIds.includes(id));

    const toggleUserSelection = (userId: string) => {
        setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
    };

    return (
        <div className="container">
            <h5 className="my-3">Assign Moods</h5>
            <div className="row">
                <div className="col-md-4">
                    <h6>Spaces</h6>
                    <div className="list-group mb-3">
                        {spaces.map((space) => (
                            <div className={`list-group-item mb-0 ${selectedSpace?._id === space._id ? 'background-primary color-light' : ''}`} key={space._id} onClick={() => { setSelectedSpace(space); setSelectedMood(space.data.moods.length > 1 ? 1 : null); setSelectedUserIds([]); }}>
                                <h6>{space.name}</h6>
                                <p className="mb-0">{space.data.moods.length} moods</p>
                            </div>
                        ))}
                    </div>
                    <h6>Moods</h6>
                    <div className="list-group mb-3">
                        {getAssignableMoodEntries(selectedSpace?.data.moods ?? []).map(({ mood, index }) => (
                            <div className={`list-group-item ${selectedMood === index ? 'background-primary color-light' : ''}`} key={mood.name} onClick={() => setSelectedMood(index)}>{mood.name}</div>
                        ))}
                    </div>
                </div>
                <div className="col-md-3">
                    <h6>Users</h6>
                    <div className="list-group mb-3">
                        {users
                            .filter((user) => !assignedUserIds.includes(user._id))
                            .map((user) => (
                                <div className={`list-group-item ${selectedUserIds.includes(user._id) ? 'background-primary color-light' : ''}`} key={user._id} onClick={() => toggleUserSelection(user._id)}>{user.name}</div>
                            ))}
                    </div>
                </div>
            <div className="col-md-2">
                    <div className="d-flex flex-column gap-2">
                        <Button className="btn btn-primary" disabled={loading || !selectedSpace || selectedMood === null || selectedAssignableIds.length === 0} onClick={() => {
                            if (selectedSpace && selectedMood !== null && selectedAssignableIds.length > 0) {
                                setAssigning(true);
                                dispatch(AssignMoodToUser({spaceId: selectedSpace._id, moodIndex: selectedMood, users: selectedAssignableIds}));
                            }
                        }}>{assigning ? 'Assigning...' : 'Assign'}</Button>
                        <Button className="btn btn-primary" disabled={loading || !selectedSpace || selectedMood === null || selectedUnassignableIds.length === 0} onClick={() => {
                            if (selectedSpace && selectedMood !== null && selectedUnassignableIds.length > 0) {
                                setUnassigning(true);
                                dispatch(UnassignMoodFromUser({spaceId: selectedSpace._id, moodIndex: selectedMood, users: selectedUnassignableIds}));
                            }
                        }}>{unassigning ? 'Unassigning...' : 'Unassign'}</Button>
                    </div>
                </div>
                <div className="col-md-3">
                    <h6>Currently Assigned Users</h6>
                    <div className="list-group mb-3">
                        {(selectedMood === null ? [] : selectedSpace?.data.moods[selectedMood]?.users ?? []).map((userId) => {
                            const user = users.find((u) => u._id === userId);
                            return user ? (
                                <div
                                    className={`list-group-item ${selectedUserIds.includes(user._id) ? 'background-primary color-light' : ''}`}
                                    key={user._id}
                                    onClick={() => toggleUserSelection(user._id)}
                                >
                                    {user.name}
                                </div>
                            ) : null;
                        })}
                    </div>
                </div>
            </div>
        </div>
    )
}
